import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ClipboardList,
  Heart,
  Loader2,
  PartyPopper,
  Pencil,
  Sparkles,
} from "lucide-react";

const PEP_LEVELS = ["Beginning", "Developing", "Proficient", "Highly Proficient"] as const;
const NO_LEVEL = "not-sure";

const SUBJECT_FIELDS = [
  { key: "mathematics", label: "Mathematics" },
  { key: "languageArts", label: "Language Arts" },
  { key: "science", label: "Science" },
  { key: "socialStudies", label: "Social Studies" },
  { key: "abilityTest", label: "Ability Test" },
  { key: "overall", label: "Overall" },
] as const;

type SubjectKey = (typeof SUBJECT_FIELDS)[number]["key"];

const EXAM_GRADES = [
  { value: "6", label: "Grade 6 PEP" },
  { value: "5", label: "Grade 5 Performance Task" },
  { value: "4", label: "Grade 4 Literacy and Numeracy" },
] as const;

interface OutcomeSubject {
  level?: string | null;
  score?: number | null;
}

interface Outcome {
  id: string;
  examYear: number;
  examGrade: number;
  placementSchool: string | null;
  subjects: Partial<Record<SubjectKey, OutcomeSubject>> | null;
  reportedBy: "student" | "parent";
  consent: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const scoreSchema = z
  .string()
  .optional()
  .refine(
    (v) => !v || v.trim() === "" || (/^\d{1,3}$/.test(v.trim()) && Number(v.trim()) <= 100),
    { message: "Use a number from 0 to 100" },
  );

const subjectSchema = z.object({
  level: z.string().optional(),
  score: scoreSchema,
});

const formSchema = z
  .object({
    reportedBy: z.enum(["student", "parent"]),
    examYear: z.string().min(4, "Pick the year"),
    examGrade: z.enum(["4", "5", "6"]),
    placementSchool: z.string().max(160, "That school name is a bit too long").optional(),
    notes: z.string().max(500, "Please keep your note under 500 characters").optional(),
    consent: z.boolean(),
    subjects: z.object({
      mathematics: subjectSchema,
      languageArts: subjectSchema,
      science: subjectSchema,
      socialStudies: subjectSchema,
      abilityTest: subjectSchema,
      overall: subjectSchema,
    }),
  })
  .refine((v) => v.consent === true, {
    message: "Please tick the box so we know it is okay to store these results.",
    path: ["consent"],
  })
  .refine(
    (v) =>
      Object.values(v.subjects).some(
        (s) => (s.level && s.level !== NO_LEVEL) || (s.score && s.score.trim() !== ""),
      ),
    {
      message: "Fill in at least one subject so we know how it went.",
      path: ["subjects"],
    },
  );

type FormValues = z.infer<typeof formSchema>;

const emptySubjects = () =>
  SUBJECT_FIELDS.reduce(
    (acc, s) => {
      acc[s.key] = { level: NO_LEVEL, score: "" };
      return acc;
    },
    {} as Record<SubjectKey, { level: string; score: string }>,
  );

function defaultValues(year: number): FormValues {
  return {
    reportedBy: "student",
    examYear: String(year),
    examGrade: "6",
    placementSchool: "",
    notes: "",
    consent: false,
    subjects: emptySubjects(),
  };
}

function levelTone(level: string | null | undefined): string {
  switch (level) {
    case "Highly Proficient":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
    case "Proficient":
      return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
    case "Developing":
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
    case "Beginning":
      return "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function examGradeLabel(grade: number): string {
  return EXAM_GRADES.find((g) => Number(g.value) === grade)?.label ?? `Grade ${grade}`;
}

export default function PepResults() {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const formRef = useRef<HTMLDivElement | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () => Array.from({ length: 5 }, (_, i) => currentYear - i),
    [currentYear],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues(currentYear),
    mode: "onSubmit",
  });

  const examGrade = form.watch("examGrade");
  const consent = form.watch("consent");

  const { data: outcomes = [], isLoading: outcomesLoading } = useQuery<Outcome[]>({
    queryKey: ["/api/outcomes/mine"],
    enabled: isAuthenticated && !!user,
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const subjects: Record<string, OutcomeSubject> = {};
      for (const field of SUBJECT_FIELDS) {
        const entry = values.subjects[field.key];
        const level = entry?.level && entry.level !== NO_LEVEL ? entry.level : null;
        const rawScore = entry?.score?.trim();
        const score = rawScore ? Number(rawScore) : null;
        if (level || score !== null) subjects[field.key] = { level, score };
      }
      const res = await apiRequest("POST", "/api/outcomes", {
        examYear: Number(values.examYear),
        examGrade: Number(values.examGrade),
        placementSchool: values.examGrade === "6" ? values.placementSchool?.trim() || null : null,
        subjects,
        notes: values.notes?.trim() || null,
        reportedBy: values.reportedBy,
        consent: true,
      });
      return (await res.json()) as Outcome;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/outcomes/mine"] });
      setJustSaved(true);
      toast({
        title: "Thank you!",
        description: "Every result you share makes EduFocal smarter for the next child.",
      });
      form.reset(defaultValues(currentYear));
    },
    onError: (error: any) => {
      toast({
        title: "We could not save that",
        description:
          error?.message?.replace(/^\d+:\s*/, "") ||
          "Something went wrong. Please check the form and try again.",
        variant: "destructive",
      });
    },
  });

  const startUpdate = (outcome: Outcome) => {
    const subjects = emptySubjects();
    for (const field of SUBJECT_FIELDS) {
      const entry = outcome.subjects?.[field.key];
      subjects[field.key] = {
        level: entry?.level ?? NO_LEVEL,
        score: entry?.score === null || entry?.score === undefined ? "" : String(entry.score),
      };
    }
    form.reset({
      reportedBy: outcome.reportedBy === "parent" ? "parent" : "student",
      examYear: String(outcome.examYear),
      examGrade: ([4, 5, 6].includes(outcome.examGrade)
        ? String(outcome.examGrade)
        : "6") as "4" | "5" | "6",
      placementSchool: outcome.placementSchool ?? "",
      notes: outcome.notes ?? "",
      consent: false,
      subjects,
    });
    setJustSaved(false);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
        <div className="container mx-auto p-4 md:p-8 max-w-3xl space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const subjectsError = form.formState.errors.subjects?.message;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10">
      <div className="container mx-auto p-4 md:p-8 max-w-3xl">
        <Button
          variant="ghost"
          size="sm"
          className="mb-3 -ml-2"
          onClick={() => setLocation("/report-card")}
          data-testid="button-back-report-card"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to my progress
        </Button>

        <div className="mb-6 text-center animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-1.5 rounded-full mb-3 flex-wrap">
            <ClipboardList className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Your real results</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" data-testid="text-page-title">
            Tell us how your exam went
          </h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-xl mx-auto">
            When you share your real results, we learn which practice truly helps. That makes the
            study plan better for you and for every other Jamaican student on EduFocal.
          </p>
        </div>

        {justSaved && (
          <Card
            className="mb-6 border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/70 dark:bg-emerald-950/30 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both"
            data-testid="card-save-success"
          >
            <CardContent className="p-4 flex items-start gap-3 flex-wrap">
              <PartyPopper className="w-6 h-6 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">Thank you!</p>
                <p className="text-xs text-muted-foreground">
                  Every result you share makes EduFocal smarter for the next child.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div ref={formRef}>
          <Card className="mb-6 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">Add your results</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Fill in what you know. You can leave anything blank.
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit((values) => saveMutation.mutate(values))}
                  className="space-y-5"
                >
                  <FormField
                    control={form.control}
                    name="reportedBy"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Who is filling this in?</FormLabel>
                        <FormControl>
                          <RadioGroup
                            value={field.value}
                            onValueChange={field.onChange}
                            className="flex flex-wrap gap-3"
                          >
                            <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                              <RadioGroupItem value="student" id="reported-by-student" data-testid="radio-reported-student" />
                              <Label htmlFor="reported-by-student" className="cursor-pointer font-normal">
                                I am the student
                              </Label>
                            </div>
                            <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                              <RadioGroupItem value="parent" id="reported-by-parent" data-testid="radio-reported-parent" />
                              <Label htmlFor="reported-by-parent" className="cursor-pointer font-normal">
                                I am the parent or guardian
                              </Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="examYear"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Exam year</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger data-testid="select-exam-year">
                                <SelectValue placeholder="Pick a year" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {years.map((y) => (
                                <SelectItem key={y} value={String(y)}>
                                  {y}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="examGrade"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Which exam?</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger data-testid="select-exam-grade">
                                <SelectValue placeholder="Pick an exam" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {EXAM_GRADES.map((g) => (
                                <SelectItem key={g.value} value={g.value}>
                                  {g.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {examGrade === "6" && (
                    <FormField
                      control={form.control}
                      name="placementSchool"
                      render={({ field }) => (
                        <FormItem className="animate-in fade-in duration-300">
                          <FormLabel>Which high school did you get? (optional)</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="For example, Campion College"
                              {...field}
                              value={field.value ?? ""}
                              data-testid="input-placement-school"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium">Your subject results</p>
                      <p className="text-xs text-muted-foreground">
                        Add a performance level, a score, or both. Skip anything you do not have.
                      </p>
                    </div>
                    <div className="space-y-3">
                      {SUBJECT_FIELDS.map((subject) => (
                        <div
                          key={subject.key}
                          className="rounded-md border bg-muted/40 p-3 space-y-2"
                          data-testid={`group-subject-${subject.key}`}
                        >
                          <p className="text-sm font-medium">{subject.label}</p>
                          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                            <FormField
                              control={form.control}
                              name={`subjects.${subject.key}.level` as const}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-xs text-muted-foreground">
                                    Performance level
                                  </FormLabel>
                                  <Select
                                    value={field.value || NO_LEVEL}
                                    onValueChange={field.onChange}
                                  >
                                    <FormControl>
                                      <SelectTrigger data-testid={`select-level-${subject.key}`}>
                                        <SelectValue placeholder="Not sure" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value={NO_LEVEL}>Not sure</SelectItem>
                                      {PEP_LEVELS.map((level) => (
                                        <SelectItem key={level} value={level}>
                                          {level}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name={`subjects.${subject.key}.score` as const}
                              render={({ field }) => (
                                <FormItem className="sm:w-36">
                                  <FormLabel className="text-xs text-muted-foreground">
                                    Score (optional)
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      inputMode="numeric"
                                      placeholder="0 to 100"
                                      {...field}
                                      value={field.value ?? ""}
                                      data-testid={`input-score-${subject.key}`}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {subjectsError && (
                      <p className="text-sm font-medium text-destructive" data-testid="text-subjects-error">
                        {subjectsError}
                      </p>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Anything else you want to tell us? (optional)</FormLabel>
                        <FormControl>
                          <Textarea
                            rows={3}
                            placeholder="For example, how you felt on exam day."
                            {...field}
                            value={field.value ?? ""}
                            data-testid="input-notes"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="consent"
                    render={({ field }) => (
                      <FormItem className="rounded-md border bg-muted/40 p-3">
                        <div className="flex items-start gap-3">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) => field.onChange(checked === true)}
                              className="mt-0.5"
                              data-testid="checkbox-consent"
                            />
                          </FormControl>
                          <div className="space-y-1">
                            <FormLabel className="font-normal leading-snug cursor-pointer">
                              I agree that EduFocal Academy may store these results and use them,
                              together with practice history, to improve predictions and study plans
                              for Jamaican students. You can ask us to delete them at any time.
                            </FormLabel>
                            <FormDescription className="text-xs">
                              Your results are never shown on leaderboards or shared with other
                              students.
                            </FormDescription>
                            <FormMessage />
                          </div>
                        </div>
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full sm:w-auto"
                    disabled={!consent || saveMutation.isPending}
                    data-testid="button-save-results"
                  >
                    {saveMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving
                      </>
                    ) : (
                      <>
                        <Heart className="h-4 w-4 mr-2" /> Save my results
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>

        <Card className="animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Results you have shared</CardTitle>
            <p className="text-xs text-muted-foreground">
              One record for each exam year. Sharing again updates the record you already sent.
            </p>
          </CardHeader>
          <CardContent className="pt-0">
            {outcomesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : outcomes.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-outcomes-empty">
                You have not shared any results yet. When you do, they will show up here.
              </p>
            ) : (
              <div className="space-y-3">
                {outcomes.map((outcome) => {
                  const entries = SUBJECT_FIELDS.map((f) => ({
                    ...f,
                    value: outcome.subjects?.[f.key],
                  })).filter(
                    (e) =>
                      e.value &&
                      (e.value.level || typeof e.value.score === "number"),
                  );
                  return (
                    <div
                      key={outcome.id}
                      className="rounded-md border p-3 space-y-2"
                      data-testid={`card-outcome-${outcome.examYear}-${outcome.examGrade}`}
                    >
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-sm font-medium">
                            {examGradeLabel(outcome.examGrade)} &middot; {outcome.examYear}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Shared by {outcome.reportedBy === "parent" ? "a parent" : "the student"}
                            {outcome.placementSchool ? ` · ${outcome.placementSchool}` : ""}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startUpdate(outcome)}
                          data-testid={`button-update-${outcome.examYear}-${outcome.examGrade}`}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Update
                        </Button>
                      </div>
                      {entries.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {entries.map((e) => (
                            <Badge
                              key={e.key}
                              variant="secondary"
                              className={`text-xs font-normal ${levelTone(e.value?.level)}`}
                            >
                              {e.label}
                              {e.value?.level ? `: ${e.value.level}` : ""}
                              {typeof e.value?.score === "number" ? ` (${e.value.score})` : ""}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {outcome.notes && (
                        <p className="text-xs text-muted-foreground italic">{outcome.notes}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
